import React, { useState } from 'react';
import { CheckCircle2, Download, MoreVertical, PlusSquare, Share, Smartphone } from 'lucide-react';

export type GuidePlatform = 'android' | 'ios';

export interface InstallGuideProps {
  /** Tab to open first (the visitor's own platform when known). */
  initialPlatform?: GuidePlatform;
}

interface Step {
  icon: React.ReactNode;
  text: React.ReactNode;
}

const ANDROID_STEPS: Step[] = [
  { icon: <Smartphone size={22} />, text: <>Open <strong>Chrome</strong> and go to the pelora sign-in page.</> },
  { icon: <Download size={22} />, text: <>Tap <strong>Install pelora app</strong> below the sign-in form, then <strong>Install</strong>.</> },
  { icon: <MoreVertical size={22} />, text: <>No button? Open Chrome's menu <strong>(⋮)</strong> and tap <strong>Install app</strong> or <strong>Add to Home screen</strong>, then <strong>Install</strong>.</> },
  { icon: <CheckCircle2 size={22} />, text: <>Open <strong>pelora</strong> from its icon on your home screen or in your app list.</> },
];

const SAMSUNG_STEPS: Step[] = [
  { icon: <MoreVertical size={22} />, text: <>In <strong>Samsung Internet</strong>, open the menu <strong>(≡)</strong>, tap <strong>Add page to</strong>, then <strong>Home screen</strong>.</> },
];

const IOS_STEPS: Step[] = [
  { icon: <Smartphone size={22} />, text: <>Open <strong>Safari</strong> and go to the pelora sign-in page.</> },
  { icon: <Share size={22} />, text: <>Tap the <strong>Share</strong> button (a square with an arrow). On iPhone it's at the bottom of the screen; on iPad it's at the top right.</> },
  { icon: <PlusSquare size={22} />, text: <>Scroll down and tap <strong>Add to Home Screen</strong>. Keep the name <strong>pelora</strong> and tap <strong>Add</strong>.</> },
  { icon: <CheckCircle2 size={22} />, text: <>Open <strong>pelora</strong> from your Home Screen.</> },
];

const StepList: React.FC<{ steps: Step[]; start?: number }> = ({ steps, start = 1 }) => (
  <ol className="space-y-4" start={start}>
    {steps.map((step, i) => (
      <li key={i} className="flex items-start gap-3 text-base text-gray-800">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white" aria-hidden="true">{start + i}</span>
        <span className="mt-1 shrink-0 text-sky-700" aria-hidden="true">{step.icon}</span>
        <span className="pt-0.5">{step.text}</span>
      </li>
    ))}
  </ol>
);

/**
 * Step-by-step instructions for installing pelora on a phone or tablet (Phase 16A, design §4).
 * Android (Chrome, Samsung Internet) and iPhone / iPad (Safari) tabs, then what to do next.
 */
const InstallGuide: React.FC<InstallGuideProps> = ({ initialPlatform = 'android' }) => {
  const [platform, setPlatform] = useState<GuidePlatform>(initialPlatform);
  const tab = (value: GuidePlatform, label: string) => (
    <button
      type="button"
      role="tab"
      id={`install-tab-${value}`}
      aria-selected={platform === value}
      aria-controls={`install-panel-${value}`}
      onClick={() => setPlatform(value)}
      className={`min-h-[44px] flex-1 rounded-lg px-3 text-sm font-semibold ${platform === value ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-100'}`}
    >
      {label}
    </button>
  );

  return (
    <div>
      <div role="tablist" aria-label="Device type" className="mb-5 flex gap-2 rounded-xl bg-slate-100 p-1">
        {tab('android', 'Android')}
        {tab('ios', 'iPhone & iPad')}
      </div>

      {platform === 'android' ? (
        <div role="tabpanel" id="install-panel-android" aria-labelledby="install-tab-android" className="space-y-5">
          <StepList steps={ANDROID_STEPS} />
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="mb-2 text-sm font-semibold text-gray-700">Using Samsung Internet?</p>
            <StepList steps={SAMSUNG_STEPS} start={2} />
          </div>
        </div>
      ) : (
        <div role="tabpanel" id="install-panel-ios" aria-labelledby="install-tab-ios" className="space-y-5">
          <StepList steps={IOS_STEPS} />
          <p className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            Use <strong>Safari</strong>. Other iPhone browsers may not offer Add to Home Screen.
          </p>
        </div>
      )}

      <section className="mt-6 space-y-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950">
        <h3 className="text-base font-semibold">After installing</h3>
        <ul className="list-disc space-y-1 pl-5">
          <li>Always open pelora from its <strong>icon</strong>, not from a browser tab. Offline mode only works in the installed app.</li>
          <li>Sign in, then on <strong>My Day</strong> tap <strong>Set up offline mode</strong> and choose a 6-digit PIN (offline mode is included with the Core plan and above).</li>
          <li>Open pelora at least once a day while you have signal so your patients stay up to date.</li>
        </ul>
      </section>

      <section className="mt-6 space-y-2 text-sm text-gray-700">
        <h3 className="text-base font-semibold text-gray-900">Don't see the install option?</h3>
        <ul className="list-disc space-y-1 pl-5">
          <li>If you opened the link from email or a chat app, open it in <strong>Chrome</strong> (Android) or <strong>Safari</strong> (iPhone / iPad) instead.</li>
          <li>Private / incognito tabs can't install apps. Use a normal tab.</li>
          <li>Update your browser and your phone's software, then try again.</li>
          <li>Already installed? Look for the <strong>pelora</strong> icon on your home screen or in your app list.</li>
        </ul>
      </section>
    </div>
  );
};

export default InstallGuide;
